import { ErrorCard, FormGroup } from '@starsoft/common/components';
import { PaginatedResponse, UserEmail } from '@starsoft/common/models';
import { MethodSelectProps } from '../props';
import { useCallback, useMemo } from 'react';

import ConfirmationMethodCard from '../Card';

import styles from './styles.module.scss';
import { useUserEmails } from '../../../../api';
import { useTranslation } from '../../../../hooks';

/**
 * Component that renders a list of user email confirmation methods to select from
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.callback - Callback function called when an email is selected
 * @param {bigint} props.currentId - Currently selected email ID
 * @returns {JSX.Element} Form group containing email selection cards
 */
export default function ConfirmationMethodSelectEmail({
  callback,
  currentId,
}: MethodSelectProps) {
  const { t } = useTranslation('common');

  /**
   * Callback function called when emails are successfully fetched
   * Automatically selects the primary email if available
   *
   * @param {PaginatedResponse<UserEmail>} data - Paginated response containing user emails
   */
  function externalOnSuccess(data: PaginatedResponse<UserEmail>) {
    callback(data?.data?.find(userEmail => userEmail?.primary)?.id);
  }

  // Hook to fetch user emails with success callback
  const { emails, isLoading, error, refetch } = useUserEmails({
    externalOnSuccess,
  });

  /**
   * Handles selection of an email method
   * Calls the parent callback with selected email ID
   *
   * @param {bigint} emailId - ID of the selected email
   */
  const handleSetUserEmail = useCallback(
    (emailId: bigint) => {
      callback(emailId);
    },
    [callback],
  );

  /**
   * Memoized list of email selection cards
   * Each card displays an email address and handles selection
   */
  const Cards = useMemo(
    () =>
      emails?.data?.map(email => (
        <ConfirmationMethodCard
          label={email?.email}
          value={email?.id}
          onClick={handleSetUserEmail}
          selected={email?.id == currentId}
          key={`confirmation-method-${email?.id} `}
        />
      )),
    [emails, currentId, handleSetUserEmail],
  );

  /**
   * Memoized list of skeleton loading cards
   * Displayed while emails are being fetched
   */
  const Skeletons = useMemo(
    () =>
      Array.from({ length: 3 })?.map((_, index) => (
        <ConfirmationMethodCard
          key={`confirmation-method-email-skeleton-${index} `}
          isLoading
        />
      )),
    [],
  );

  // Show error card if email fetch failed
  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  return (
    <FormGroup
      label={t('select_method_label', {
        method: t('email_label'),
      })}
      isLoading={isLoading}
      loading={{ label: true, secondary: false }}
    >
      <div className={styles.container}>{isLoading ? Skeletons : Cards}</div>
    </FormGroup>
  );
}
